import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import { auth, functions } from './firebase';

/**
 * 기사앱 callable 래퍼.
 *
 * businweb 은 Firestore 를 안 쓴다 — docuservice 처럼 세션 문서를 실시간 구독할 수 없다.
 * 대신 서버(`requireDriver`)가 **매 호출마다** 세션을 대조한다. 다른 기기에서 다시 로그인하면
 * 옛 토큰이 거부되므로, 여기서 `permission-denied` 를 잡아 **스스로 로그아웃**한다
 * (→ 로그인 화면으로 떨어지고, evicted 안내가 뜬다).
 */

export const EVICTED_KEY = 'businweb.driver.evicted';

/**
 * 차주가 고른 「누구 것으로」(#258) — null 이면 본인. 모든 호출에 `asDriverId` 로 실어 보낸다.
 * 받는 쪽은 명세서·세금계산서·4종 신청뿐이고(`driverFleet.actingDriver` — 차주 차량 편성인지 서버가 다시 본다),
 * 나머지(입찰·공지 등)는 이 값을 읽지 않는다 = 늘 본인.
 */
let actingDriverId: number | null = null;
export const getActingDriverId = () => actingDriverId;
export function setActingDriverId(id: number | null): void {
  actingDriverId = id;
}

function driverCall<Req, Res>(name: string) {
  const fn = httpsCallable<Req, Res>(functions, name);
  return async (data?: Req): Promise<Res> => {
    try {
      const payload = actingDriverId === null ? data : ({ ...(data ?? {}), asDriverId: actingDriverId } as Req);
      const res: HttpsCallableResult<Res> = await fn(payload as Req);
      return res.data;
    } catch (e) {
      const code = (e as { code?: string }).code ?? '';
      if (code.endsWith('permission-denied')) {
        // 세션이 밀렸거나 만료됐다 — 조용히 로그아웃하고 다시 로그인하게 한다.
        sessionStorage.setItem(EVICTED_KEY, '다른 기기에서 로그인되어 로그아웃되었습니다. 다시 로그인해 주세요.');
        await signOut(auth).catch(() => {});
      }
      throw e;
    }
  };
}

export interface DriverProfile {
  driver_id: number;
  driver_name: string | null;
  driver_kind: string | null;
  phone: string | null;
}

export interface DriverAlloc {
  car_alloc_id: number;
  car_id: number | null;
  grp_id: number | null;
  car_no: string | null;
  car_name: string | null;
  grp_name: string | null;
  owner_name: string | null;
}

/** 입찰 신청 때 고를 차량 — 관리기사B 차주면 자기 차량 전부(다른 기사가 모는 차 = other_driver · 10-30 way). */
export interface BidAlloc extends DriverAlloc { driver_name: string | null; other_driver: boolean }

/** 차주 차량의 다른 기사(#258) — 「누구 것으로」 고르는 목록. */
export interface FleetDriver {
  driver_id: number;
  driver_name: string;
  cars: { car_id: number; car_no: string | null }[];
}

export interface OpenBid {
  dispt_id: number;
  recv_id: number | null;
  cust_name: string | null;
  dispt_name: string | null;
  car_kind: string | null;
  car_cnt: number | null;
  trip_type: string | null;
  start_loc: string | null;
  start_time: string | null;
  end_loc: string | null;
  end_time: string | null;
  /** #409 왕복 복편 — 복귀 출발 · 복귀 도착 */
  end_dept_time?: string | null;
  start_arrv_time?: string | null;
  bidding_type: string | null;
  close_date: string | null;
  mine_pending: boolean | null;
  /** 노선 참고사항(#275) */
  request?: string | null;
  /** 대수가 다 찬 입찰 — 「입찰 대수가 다 차서 마감」(신청 불가) */
  bid_full?: boolean | null;
  /** 입찰 금액 = 1대당 운행료(문자열 · 관리기사A 는 null) */
  cost_driver?: string | null;
  /** #386 이미 이 노선에 배정됨 */
  mine_assigned?: boolean | null;
  /** #392 배정된 대수(남은 대수 = car_cnt − 이것) */
  assigned_cnt?: number | null;
  /** #391 접수 담당자 — 취소 승인 전화 */
  owner_name?: string | null;
  owner_phone?: string | null;
}

/** 내 배차 — 배정된 일반 운행 한 줄. */
export interface MyDispt {
  dispt_car_sch_id: number;
  dispt_id: number;
  cust_name: string | null;
  dispt_name: string | null;
  trip_type: string | null;
  start_loc: string | null;
  end_loc: string | null;
  start_time: string | null;
  end_time: string | null;
  car_no: string | null;
  request: string | null;
  /** 1대분 운행료 — 관리기사A 면 서버가 null 로 보낸다. */
  cost_driver: string | null;
  /** 그 달 운행료 합(줄마다 같은 값) — 관리기사A 면 null. */
  month_total: string | null;
  /** 배정된 기사(10-30 · 차주는 자기 차량 관리기사B 줄도 받는다). 옛 서버면 없다. */
  driver_id?: number;
  driver_name?: string | null;
  /** 본인이 아닌 기사 줄 — 「○○ 기사」 표시. */
  other_driver?: boolean;
  /** 그 기사 몫 그 달 운행료 합(줄마다 같은 값) — 차량을 고르면 이 값. */
  driver_total?: string | null;
}

/** 차주 「내 배차」 차량 고르기 한 칸(운행 중 편성 · 입찰 차량과 같은 목록). */
export interface MyDisptCar {
  car_alloc_id: number;
  car_id: number;
  car_no: string | null;
  driver_id: number;
  driver_name: string | null;
  other_driver: boolean;
}

export interface MyDisptPage {
  mon: string;
  rows: MyDispt[];
  /** 배차 있는 달(최근 24) — 달 고르기 칩. */
  months: { mon: string; cnt: number }[];
  showCost: boolean;
  /** 통근 운행(buslink 날짜별 배차 · 운행료 없음). 옛 서버면 없다. */
  commute?: CommuteRun[];
  /** buslink 를 못 읽었다 — 일반 배차는 그대로. */
  commuteError?: boolean;
  /** 차주면 자기 차량들(본인 먼저) — 비었으면 고르기를 안 그린다. */
  cars?: MyDisptCar[];
}

export interface CommuteRun {
  id: string;
  date: string;
  /** "HH:MM" */
  depart_time: string | null;
  route_name: string | null;
  car_no: string | null;
  /** 정류장 도착 기록이 있다(실제 운행 시작). */
  ran: boolean;
  driver_id?: number;
  driver_name?: string | null;
  other_driver?: boolean;
}

export interface MyBid {
  dispt_car_sch_wait_id: number;
  dispt_id: number | null;
  applied_at: string | null;
  is_consumed: boolean | null;
  /** 같은 노선에 신청한 횟수(노선 하나 = 카드 하나로 묶는다). */
  apply_cnt?: number | null;
  cust_name: string | null;
  dispt_name: string | null;
  start_time: string | null;
  start_loc: string | null;
  end_loc: string | null;
  close_date: string | null;
  bidding_type: string | null;
  dispt_status: string | null;
  assigned: boolean | null;
}

/**
 * 금액은 문자열이다 — 숫자로 왕복시키면 소수 4자리가 깎인다.
 *
 * ⚠️ 공개 전인 달은 **금액이 전부 `null`** 로 온다(서버가 안 내려준다). 0 이 아니라 null 이다 —
 *    `won(null)` 은 "0" 을 만들므로 그대로 찍으면 **"차인지급액 0원"** 이라는 거짓말이 된다.
 *    반드시 `published` 를 먼저 보고 갈라 그릴 것.
 */
export interface MySalaryMonth {
  drive_mon: string;
  line_cnt: number | null;
  plus_amt: string | null;
  minus_amt: string | null;
  result_amt: string | null;
  /** 지금 이 기사에게 열려 있나. 공개 예약(`salary_publish`) 판정 결과. */
  published: boolean;
  /** 열리는 시각(KST 벽시계). null 이면 "시각 미정"(작업 중). */
  open_at: string | null;
}

/** 차주의 「내 차량 전체 합계」 — 차주 몫 관리기사B 줄 전부의 차인지급액(서버 계산 · 공개된 달만). */
export interface FleetSalaryMonth {
  drive_mon: string;
  result_amt: string;
  car_cnt: number;
}

export interface SalaryLine {
  sort: number | null;
  kind: string | null;
  io: string | null;
  category: string | null;
  account_name: string | null;
  amt: string | null;
  comment: string | null;
  car_id?: number | null;
  owner_id?: number | null;
}

/** 그 달 명세의 차량·차주 묶음(둘 이상일 때만 온다 — 월 중간 편성 변경). 금액은 서버 합. */
export interface SalaryGroup {
  car_id: number | null;
  owner_id: number | null;
  car_no: string | null;
  owner_name: string | null;
  line_cnt: number;
  plus_amt: string | null;
  minus_amt: string | null;
  result_amt: string | null;
  period_from: string | null;
  period_to: string | null;
}

export interface Notice {
  notice_id: number;
  category: string | null;
  subject: string | null;
  comment?: string | null;
  is_top: boolean | null;
  expire: string | null;
  grp_id: number | null;
  grp_name: string | null;
  created_at: string | null;
  is_new: boolean | null;
  is_read?: boolean;
}

export const driverApi = {
  getMe: driverCall<void, { driver: DriverProfile | null; allocs: DriverAlloc[]; isOwner?: boolean; orderOpen?: boolean; fleet?: FleetDriver[] }>('getDriverMe'),
  // 차주의 예비기사 분배(차인지급액 조정에 들어간다) — 서버가 휴대폰 번호로 차주 본인인지 가른다.
  getReserveSplit: driverCall<{ driveMon?: string; ownerId?: number }, ReserveSplitView>('getMyReserveSplit'),
  saveReserveSplit: driverCall<{ driveMon: string; ownerId: number; items: { driverId: number; amount: string }[] }, { ok: true; total: number }>('saveMyReserveSplit'),
  listBids: driverCall<{ limit?: number; offset?: number }, { rows: OpenBid[]; bidAllocs?: BidAlloc[] }>('listDriverBids'),
  listMyBids: driverCall<{ limit?: number; offset?: number }, { rows: MyBid[] }>('listMyBids'),
  listMyDispts: driverCall<{ mon?: string }, MyDisptPage>('listMyDispts'),
  applyBid: driverCall<{ disptId: number; carAllocId: number }, { bidId: number; assigned?: boolean }>('applyBid'),
  withdrawBid: driverCall<{ disptId: number }, { withdrawn: number }>('withdrawBid'),
  listSalaryMonths: driverCall<void, { rows: MySalaryMonth[]; fleet?: FleetSalaryMonth[] }>('listMySalaryMonths'),
  listSalaryLines: driverCall<
    { driveMon: string },
    { rows: SalaryLine[]; groups?: SalaryGroup[]; published?: boolean; openAt?: string | null }
  >('listMySalaryLines'),
  listNotices: driverCall<{ q?: string; limit?: number; offset?: number }, { rows: Notice[] }>('listNotices'),
  getNotice: driverCall<{ noticeId: number }, { row: Notice }>('getNotice'),
  /**
   * 내 서류. 담당자 화면과 **같은 callable** 을 쓴다 — 서버 `fileActor` 가 기사 클레임을 보고
   * `ownerId !== 본인 driver_id` 면 거부한다. 기사 전용 엔드포인트를 따로 만들면
   * 게이트가 두 벌이 되어 언젠가 갈라진다.
   */
  // 세금계산서 신청(plan/driver-orders.md ②) — 계산서는 발행대장(tax_invoice)에 바로 쌓인다.
  /** forDriverId = 다른 기사 대신 신청(#332) — 그 기사의 법인·신청자 칸. 신청 화면에만 쓴다(보기 화면엔 안 실는다). */
  invoiceForm: driverCall<{ forDriverId?: number }, DriverInvoiceForm>('getDriverInvoiceForm'),
  searchCustomers: driverCall<{ q: string }, { rows: DriverCustomer[] }>('searchDriverCustomers'),
  searchPeers: driverCall<{ q: string; internal?: boolean },{ rows: { driverId: number; label: string }[] }>('searchDriverPeers'),
  requestInvoice: driverCall<DriverInvoiceRequest, { ok: true; invoiceId: number; invoiceNo: string }>('requestDriverInvoice'),
  listMyInvoices: driverCall<{ month?: string }, { rows: MyDriverInvoice[] }>('listMyDriverInvoices'),
  cancelInvoice: driverCall<{ id: number; reason?: string }, { ok: true; kind: 'amended' }>('cancelMyDriverInvoice'),
  // ④ 페이앱·카드단말기·현금영수증·다수배분 — 표 driver_order(신청 스위치·법인·첨부는 세금계산서와 같다)
  requestOrder: driverCall<DriverOrderRequest, { ok: true; orderId: number; orderNo: string }>('requestDriverOrder'),
  listMyOrders: driverCall<void, { rows: MyDriverOrder[] }>('listMyDriverOrders'),
  cancelOrder: driverCall<{ id: number; reason?: string }, { ok: true; cancelState: string }>('cancelMyDriverOrder'),
  listMyFiles: driverCall<
    { ownerType: 'driver' | 'car'; ownerId: number },
    { ownerType: string; ownerId: number; items: DriverFile[] }
  >('listFileAssets'),
  /** 푸시 알림 토큰 등록(PWA · lib/driverPush.ts). */
  registerPush: driverCall<{ token: string; userAgent?: string }, { ok: true }>('registerDriverPush'),
  /** 서류신청·세금계산서 앱으로 로그인한 채 넘어가는 주소(2분짜리 1회용 입장권이 붙는다). */
  issueHandoff: driverCall<{ target: 'docuservice' | 'dytax' }, { url: string }>('issueDriverHandoff'),
};

export interface DriverFile {
  fileAssetId: number;
  kind: string;
  label: string;
  contentType: string;
  byteSize: number;
  createdAt: string;
}

export interface DriverCustomer { cust_name: string; biz_number: string | null; email: string | null; phone?: string | null }
export interface DriverInvoiceForm {
  open: boolean; corps: { corp_id: number; short_name: string }[]; corpFixed: boolean; recent: DriverCustomer[]; requester: string;
  /** 다른 기사 대신 신청 화면(#332) — 최근 거래처는 비어 온다. */
  proxy?: boolean;
}
export interface DriverInvoiceRequest {
  corpId: number; custName: string; bizNumber: string; email: string; phone?: string; requestDate: string;
  supply: string; vat?: string; memo?: string;
  attach?: { path: string; type: string; name: string } | null;
  dists?: { driverId: number; amount: string }[];
  /** 다른 기사 대신 신청(#332) — 신청자 = 이 기사 · 넣은 사람 = 로그인한 나. */
  forDriverId?: number;
}
export interface MyDriverInvoice {
  invoice_id: string; invoice_no: string; corp_name: string; cust_name: string; biz_number: string | null; email: string | null;
  request_date: string | null; supply: string; vat: string; total: string; memo: string | null;
  issuer: string | null; paid_date: string | null; attach_name: string | null;
  mine: boolean; legacy?: boolean; cancelled: boolean; my_share: string | null; dists: { type: string; name: string; amount: string }[];
  /** 대신 신청한 기사 이름(#332 · #258 차주 대행) · 내가 대신 신청해 준 기사 이름(내 목록에만). */
  proxy_by?: string | null; proxy_for?: string | null;
}

export type DriverOrderKind = '페이앱' | '카드단말기' | '현금영수증' | '다수배분';
export interface DriverOrderRequest {
  kind: DriverOrderKind; corpId: number; custName?: string; bizNumber?: string; cashId?: string; email?: string; phone?: string; requestDate: string;
  supply?: string; vat?: string; memo?: string;
  attach?: { path: string; type: string; name: string } | null;
  dists?: { driverId?: number; type?: '외부배분-개인' | '외부배분-법인'; name?: string; amount: string }[];
  forDriverId?: number;
}
export interface MyDriverOrder {
  order_id: string; kind: DriverOrderKind; order_no: string; corp_name: string; cust_name: string | null; cash_id: string | null;
  request_date: string | null; amount: string; issuer: string | null; paid_date: string | null; cancel_state: '신청취소' | '취소' | null;
  mine: boolean; legacy?: boolean; my_share: string | null; lines: { type: string; name: string | null; amount: string; cancelled: boolean }[];
  proxy_by?: string | null; proxy_for?: string | null;
  /** 「이름 | 차량」 — 받는 쪽 카드의 「보낸 사람」(차주가 자기 차로 나눈 다수배분은 차주 이름). */
  requester?: string | null;
}

/** 사업자등록증 올리기 —신청 전에 올리고 돌려받은 path 를 신청에 싣는다(바이트 그대로 보낸다). */
export async function uploadDriverInvoiceFile(file: File, forDriverId?: number | null): Promise<{ path: string; type: string; name: string }> {
  const user = auth.currentUser;
  if (!user) throw new Error('로그인이 필요합니다.');
  const token = await user.getIdToken();
  const base = `https://asia-northeast3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
  // 차주가 다른 기사를 골랐으면 그 기사 칸에 올린다(신청이 그 기사 이름으로 나가므로 · #258).
  // 다른 기사 대신 신청(#332)이면 그 기사 칸에(서버가 지금 편성 있는 기사인지 다시 본다).
  const qs = new URLSearchParams({ name: file.name, ...(actingDriverId !== null ? { as: String(actingDriverId) } : {}),
    ...(forDriverId ? { for: String(forDriverId) } : {}) });
  const res = await fetch(`${base}/uploadDriverInvoiceFile?${qs}`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream' }, body: file,
  });
  if (!res.ok) throw new Error((await res.text()) || '파일을 올리지 못했습니다.');
  return res.json();
}

/** FunctionsError → 사람이 읽을 문구. */
export function driverError(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

export interface ReserveSplitView {
  owners: { owner_id: number; owner_name: string }[];
  months: string[];
  driveMon: string | null;
  ownerId: number | null;
  closed?: boolean;
  /** 명세 공개 전 달 — 상한 금액은 내려오지 않고 저장 때 서버만 검사한다(#485). */
  pending?: boolean;
  /** 나눌 수 있는 최대 금액(차주 차인지급액 합계, 다른 분배 빼고). null = 그 달 명세에 이 차주가 없다(또는 공개 전). */
  limit?: number | null;
  candidates?: { driver_id: number; driver_name: string; car_no: string | null }[];
  splits?: { driver_id: number; driver_name: string | null; amount: string }[];
}
